import { useEffect } from 'react';
import { Control, FormProvider, useForm, useWatch } from 'react-hook-form';

import { Button } from '@creditornot/cb-button';
import { Modal } from '@creditornot/cbt-modal';
import { ModalFooter } from 'common/components/cookbook/Modals/ModalFooter';
import { ModalHeader } from 'common/components/cookbook/Modals/ModalHeader';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { AxiosDelistingRecommendationsError } from 'common/types';
import { CommentRow } from 'features/selection-recommendations/common/RecommendationActions/Reject/CommentRow';
import { PromotionRecommendationRejectionFormValues } from 'features/selection-recommendations/Recommendations/PromotionRecommendations/types';
import { reasons } from 'features/selection-recommendations/Recommendations/PromotionRecommendations/utils';

import { ReasonRow } from './ReasonRow';

type Props = {
  isOpen: boolean;
  rejectIsPending: boolean;
  onDismiss: () => void;
  onReject: (values: PromotionRecommendationRejectionFormValues) => void;
  selectedRecommendationCount: number;
  error?: AxiosDelistingRecommendationsError | null;
  canDownload: boolean;
};

type RejectFormFooterProps = {
  rejectIsPending: boolean;
  handleDismiss: () => void;
  control: Control<PromotionRecommendationRejectionFormValues>;
  canDownload: boolean;
};

const RejectFormFooter = ({
  rejectIsPending,
  handleDismiss,
  control,
  canDownload,
}: RejectFormFooterProps) => {
  const f = useFormatMessage();
  const reason = useWatch({ control, name: 'reason' });

  return (
    <>
      <Button variant="inverse" size="small" onClick={handleDismiss}>
        {f('common.cancel')}
      </Button>
      <Button
        variant="primary"
        size="small"
        type="submit"
        data-test-id="reject-recommendation-button"
        form="reject-form"
        disabled={!reason}
        loading={rejectIsPending}
      >
        {reasons.includes(reason) && canDownload
          ? f('selection_recommendations.reject_download_csv')
          : f('selection_recommendations.reject')}
      </Button>
    </>
  );
};

export const RejectPromotionsForm = ({
  isOpen,
  rejectIsPending,
  selectedRecommendationCount,
  onDismiss,
  onReject,
  error,
  canDownload,
}: Props) => {
  const f = useFormatMessage();

  const form = useForm<PromotionRecommendationRejectionFormValues>({
    mode: 'onChange',
    defaultValues: {
      reason: undefined,
      comment: undefined,
    },
  });

  const handleDismiss = () => {
    form.reset();
    onDismiss();
  };

  useEffect(() => {
    form.reset();
  }, [form.formState.isSubmitSuccessful, form]);

  const handleSubmit = (values: PromotionRecommendationRejectionFormValues) => {
    onReject({
      reason: values.reason,
      comment: values.comment?.trim(),
    });
  };

  return (
    <Modal
      data-test-id="reject-modal"
      sizePreset="centered:large"
      open={isOpen}
      onDismiss={handleDismiss}
      title={f('selection_recommendations.reject_recommendation')}
      HeaderComponent={({ onCloseClick, ...rest }) => (
        <ModalHeader onDismiss={onCloseClick} {...rest}>
          {f('selection_recommendations.reject_recommendation')}
        </ModalHeader>
      )}
      footer={
        <RejectFormFooter
          rejectIsPending={rejectIsPending}
          handleDismiss={handleDismiss}
          control={form.control}
          canDownload={canDownload}
        />
      }
      FooterComponent={(props) => <ModalFooter error={error} {...props} />}
    >
      <FormProvider {...form}>
        <form
          id="reject-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          data-test-id="reject-form"
        >
          <div>
            {f('selection_recommendations.reject_promotion_recommendation_description', {
              count: selectedRecommendationCount,
            })}
          </div>
          <ReasonRow />
          <CommentRow />
        </form>
      </FormProvider>
    </Modal>
  );
};
